import type { Discipline } from '@brighterly/lib-core-types/discipline/enums'
import type { DisciplineMessageSender } from '../model/enums'
import type { DisciplineDeliveryResult } from '../model/interfaces'
import type { ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

interface PaymentSendBaseOptions {
  senders: DisciplineMessageSender[]
  onStart?: () => void
  onFinish?: () => void
}

interface OrderSendOptions extends PaymentSendBaseOptions {
  orderId: number
}

interface EnrollSendOptions extends PaymentSendBaseOptions {
  disciplineId: string | number
  code: Discipline
}

const INVALID_URL = 'Unexpected error. Invalid request URL.'

/** A customer-messaging write that is not de-duplicated: the caller disables Send while it runs and never retries it. */
const send = async (
  url: string | null,
  body: Record<string, unknown>,
  options: PaymentSendBaseOptions,
): Promise<DisciplineDeliveryResult[] | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()

  options.onStart?.()

  try {
    if (!url) {
      toast.error(INVALID_URL)
      return null
    }

    const { data } = await $axios.post<ApiResponse>(url, body)

    return data.data ?? []
  } catch (err: unknown) {
    toast.error(getAxiosErrorMessage(err))
    return null
  } finally {
    options.onFinish?.()
  }
}

/** `POST /admin-api/orders/{order}/send` — the checkout link of a generated order, one result per channel. */
export const useOrderSendApi = (options: OrderSendOptions): Promise<DisciplineDeliveryResult[] | null> =>
  send(useAdminRoutes('admin.order.send', { order_id: options.orderId }), { senders: options.senders }, options)

/** `POST /admin-api/disciplines/{discipline}/enroll/send` — the discipline's pricing page, one result per channel. */
export const useDisciplineEnrollSendApi = (options: EnrollSendOptions): Promise<DisciplineDeliveryResult[] | null> =>
  send(
    useAdminRoutes('admin.disciplines.api.enroll.send', { discipline_id: options.disciplineId }),
    { senders: options.senders, code: options.code },
    options,
  )
